import React, { useState, useEffect } from 'react';
import { customersAPI } from '../services/api';

const IANA_TZ_MAP = {
  '-180': 'America/Argentina/Buenos_Aires',
  '-240': 'America/Santiago',
  '-300': 'America/Bogota',
  '0': 'UTC',
};

const toUTCDate = (s) => new Date(s.endsWith('Z') ? s : s + 'Z');

const formatDateTZ = (dateString) => {
  if (!dateString) return '';
  const offset = Number(localStorage.getItem('tzOffset') || '-240');
  const iana = IANA_TZ_MAP[String(offset)] || 'America/Santiago';
  return toUTCDate(dateString).toLocaleString('en-US', { timeZone: iana });
};

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [showCDRsModal, setShowCDRsModal] = useState(false);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [selectedCustomerCDRs, setSelectedCustomerCDRs] = useState([]);
  const [selectedCustomerUsers, setSelectedCustomerUsers] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await customersAPI.getCustomers();
      setCustomers(data);
    } catch (error) {
      console.error('Failed to load data:', error);
      alert('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingCustomer(null);
    setFormData({ name: '', description: '', is_active: true });
    setShowModal(true);
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      name: customer.name,
      description: customer.description || '',
      is_active: customer.is_active,
    });
    setShowModal(true);
  };

  const handleDelete = async (customerId) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;
    try {
      await customersAPI.deleteCustomer(customerId);
      alert('Customer deleted successfully');
      loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete customer');
    }
  };

  const handleViewCDRs = async (customer) => {
    try {
      const cdrs = await customersAPI.getCustomerCDRs(customer._id);
      setSelectedCustomerCDRs(cdrs);
      setShowCDRsModal(true);
    } catch (error) {
      alert('Failed to load customer CDRs');
    }
  };

  const handleViewUsers = async (customer) => {
    try {
      const users = await customersAPI.getCustomerUsers(customer._id);
      setSelectedCustomerUsers(users);
      setShowUsersModal(true);
    } catch (error) {
      alert('Failed to load customer users');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCustomer) {
        await customersAPI.updateCustomer(editingCustomer._id, formData);
        alert('Customer updated successfully');
      } else {
        await customersAPI.createCustomer(formData);
        alert('Customer created successfully');
      }
      setShowModal(false);
      loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save customer');
    }
  };

  if (loading) {
    return <div style={styles.container}><p>Loading...</p></div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.pageTitle}>Customer Management</h1>
        <button style={styles.createBtn} onClick={handleCreate}>Create Customer</button>
      </div>

      <div style={styles.tableSection}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.tableHeader}>
              <th style={styles.th}>Name</th>
              <th style={styles.th}>Description</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {customers.map((customer) => (
              <tr key={customer._id} style={styles.tableRow}>
                <td style={styles.td}>{customer.name}</td>
                <td style={styles.td}>{customer.description || 'N/A'}</td>
                <td style={styles.td}>
                  <span style={{ ...styles.badge, backgroundColor: customer.is_active ? '#27ae60' : '#e74c3c' }}>
                    {customer.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td style={styles.td}>
                  <button style={styles.actionBtn} onClick={() => handleEdit(customer)}>Edit</button>
                  <button style={styles.actionBtn} onClick={() => handleViewUsers(customer)}>Users</button>
                  <button style={styles.actionBtn} onClick={() => handleViewCDRs(customer)}>CDRs</button>
                  <button style={{ ...styles.actionBtn, ...styles.deleteBtn }} onClick={() => handleDelete(customer._id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h2>{editingCustomer ? 'Edit Customer' : 'Create Customer'}</h2>
            <form onSubmit={handleSubmit} style={styles.form}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Name</label>
                <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} style={styles.input} required />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Description</label>
                <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} style={{ ...styles.input, minHeight: '100px' }} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.checkboxLabel}>
                  <input type="checkbox" checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} style={styles.checkbox} /> Active
                </label>
              </div>
              <div style={styles.modalActions}>
                <button type="submit" style={styles.submitBtn}>{editingCustomer ? 'Update' : 'Create'}</button>
                <button type="button" style={styles.cancelBtn} onClick={() => setShowModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showCDRsModal && (
        <div style={styles.modal}>
          <div style={{ ...styles.modalContent, maxWidth: '800px' }}>
            <h2>Customer CDRs</h2>
            {selectedCustomerCDRs.length === 0 ? (
              <p>No CDRs found for this customer.</p>
            ) : (
              <table style={styles.table}>
                <thead>
                  <tr style={styles.tableHeader}>
                    <th style={styles.th}>Source</th>
                    <th style={styles.th}>Destination</th>
                    <th style={styles.th}>Start Time</th>
                    <th style={styles.th}>Duration</th>
                    <th style={styles.th}>Disposition</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedCustomerCDRs.map((cdr, index) => (
                    <tr key={index} style={styles.tableRow}>
                      <td style={styles.td}>{cdr.source}</td>
                      <td style={styles.td}>{cdr.destination}</td>
                      <td style={styles.td}>{formatDateTZ(cdr.start_time)}</td>
                      <td style={styles.td}>{cdr.duration}s</td>
                      <td style={styles.td}>{cdr.disposition || 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div style={{ marginTop: '1rem' }}>
              <button style={styles.cancelBtn} onClick={() => setShowCDRsModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {showUsersModal && (
        <div style={styles.modal}>
          <div style={{ ...styles.modalContent, maxWidth: '600px' }}>
            <h2>Customer Users</h2>
            {selectedCustomerUsers.length === 0 ? (
              <p>No users assigned to this customer.</p>
            ) : (
              <table style={styles.table}>
                <thead>
                  <tr style={styles.tableHeader}>
                    <th style={styles.th}>Username</th>
                    <th style={styles.th}>Full Name</th>
                    <th style={styles.th}>Email</th>
                    <th style={styles.th}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedCustomerUsers.map((u) => (
                    <tr key={u.id} style={styles.tableRow}>
                      <td style={styles.td}>{u.username}</td>
                      <td style={styles.td}>{u.full_name}</td>
                      <td style={styles.td}>{u.email}</td>
                      <td style={styles.td}>
                        <span style={{ ...styles.badge, backgroundColor: u.is_active ? '#27ae60' : '#e74c3c' }}>
                          {u.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div style={{ marginTop: '1rem' }}>
              <button style={styles.cancelBtn} onClick={() => setShowUsersModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: { maxWidth: '1400px', margin: '0 auto', padding: '2rem' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' },
  pageTitle: { color: '#60a5fa', margin: 0, fontWeight: '700', textShadow: '0 0 10px rgba(96, 165, 250, 0.3)' },
  createBtn: { padding: '0.75rem 1.5rem', backgroundColor: '#60a5fa', backgroundImage: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', cursor: 'pointer', boxShadow: '0 2px 8px rgba(96, 165, 250, 0.3)' },
  tableSection: { backgroundColor: '#1f2937', padding: '1.5rem', borderRadius: '12px', border: '1px solid #374151', overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: '600px' },
  tableHeader: { backgroundColor: '#111827', backgroundImage: 'linear-gradient(135deg, #111827 0%, #1f2937 100%)', borderBottom: '2px solid #60a5fa' },
  th: { padding: '1rem', textAlign: 'left', fontWeight: '600', letterSpacing: '0.3px', color: '#e2e8f0' },
  tableRow: { borderBottom: '1px solid #374151' },
  td: { padding: '1rem', color: '#9ca3af' },
  badge: { padding: '0.25rem 0.75rem', borderRadius: '12px', color: 'white', fontSize: '12px', fontWeight: '600' },
  actionBtn: { padding: '0.5rem 0.8rem', backgroundColor: '#3498db', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', marginRight: '0.4rem', fontSize: '13px' },
  deleteBtn: { backgroundColor: '#e74c3c' },
  modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '1rem' },
  modalContent: { backgroundColor: '#1f2937', padding: '1.5rem', borderRadius: '12px', width: '90%', maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto', border: '1px solid #374151', color: '#e2e8f0' },
  form: { display: 'flex', flexDirection: 'column', gap: '1rem' },
  formGroup: { display: 'flex', flexDirection: 'column' },
  label: { marginBottom: '0.5rem', color: '#9ca3af', fontWeight: '500', fontSize: '14px' },
  input: { padding: '0.75rem', border: '1px solid #374151', borderRadius: '8px', fontSize: '16px', backgroundColor: '#111827', color: '#e2e8f0', outline: 'none' },
  checkboxLabel: { display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#e2e8f0', fontWeight: '500' },
  checkbox: { width: '20px', height: '20px' },
  modalActions: { display: 'flex', gap: '1rem', marginTop: '1rem' },
  submitBtn: { flex: 1, padding: '0.75rem', backgroundColor: '#27ae60', backgroundImage: 'linear-gradient(135deg, #27ae60 0%, #219a52 100%)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', cursor: 'pointer' },
  cancelBtn: { flex: 1, padding: '0.75rem', backgroundColor: '#374151', color: '#e2e8f0', border: '1px solid #4b5563', borderRadius: '8px', fontSize: '16px', cursor: 'pointer' },
};

export default Customers;
